import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Card,
  Checkbox,
  Chip,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
  ComboboxMenuButton,
  DraggableItem,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import { useBudgetingStructuresApi } from '../../../../../../api/queries/useBudgetingStructuresApi.js';
import { usePreviewActivityListApi } from '../../../../../../api/queries/usePreviewActivityListApi.js';
import BudgetStructureSkeleton from './BudgetStructureSkeleton.jsx';

// Remove when the Budget Structure feature is fully implemented
const isBudgetStructureFeatureDisabled = true;

const BudgetStructureCards = () => {
  const { t } = useTranslation('@myn/planning_v2');

  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const { data: budgetingStructures, isLoading: isBudgetingStructuresLoading } =
    useBudgetingStructuresApi();
  const { data: activityList, isLoading: isActivityListLoading } =
    usePreviewActivityListApi();

  const activityOptions = useMemo(() => {
    return (
      activityList?.items.map((activity) => ({
        label: activity.name,
        value: activity.id,
      })) ?? []
    );
  }, [activityList]);

  if (isBudgetingStructuresLoading || isActivityListLoading) {
    return <BudgetStructureSkeleton />;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      {budgetingStructures?.map((structure) => (
        <Card key={structure.id} className="flex w-full flex-col gap-4 p-3">
          <div className="flex justify-between">
            <h6 className={cn(typographyVariants({ variant: 'h6' }))}>
              {structure.levels.join(' > ')}
            </h6>
            {structure.isDefault && (
              <Chip variant="outline" size="small">
                <span>{t('Default')}</span>
              </Chip>
            )}
          </div>
          <div>
            <span
              className={cn(
                typographyVariants({ variant: 'label01', color: 'secondary' }),
                'mb-2 flex',
              )}
            >
              {t('Assigned activities')}
            </span>

            <ComboboxInputRoot>
              <ComboboxInput
                value={activityOptions.map((option) => option.label).join(', ')}
                size="small"
                popoverClassName="z-popover"
                disabled={isBudgetStructureFeatureDisabled || isReadOnly}
              >
                <ComboboxMenu withSearch>
                  <ComboboxMenuButton
                    selectedOptions={structure.assignedActivities}
                    options={activityOptions}
                    // Implement later
                    onClearAll={() => null}
                    onSelectAll={() => null}
                  />
                  {activityOptions.map((option) => (
                    <ComboboxItem key={option.value} value={option.value}>
                      <Checkbox
                        checked={structure.assignedActivities.includes(
                          option.value,
                        )}
                      />
                      <Label variant={'bodyShort02'} htmlFor={option.value}>
                        {option.label}
                      </Label>
                    </ComboboxItem>
                  ))}
                </ComboboxMenu>
              </ComboboxInput>
            </ComboboxInputRoot>
          </div>
          <div>
            <span
              className={cn(
                typographyVariants({ variant: 'label01', color: 'secondary' }),
                'my-2 flex',
              )}
            >
              {t('Levels')}
            </span>
            <div className="flex flex-col gap-2">
              {structure.levels.map((level, index) => (
                <Tooltip key={level}>
                  <TooltipTrigger asChild>
                    <DraggableItem
                      className="w-auto"
                      style={{ marginLeft: `${index * 2}rem` }}
                      draggableIconPosition="right"
                      disabled={isBudgetStructureFeatureDisabled || isReadOnly}
                      title={level}
                      aria-label={level}
                    />
                  </TooltipTrigger>
                  <TooltipContent side="left" className="z-tooltip">
                    {t('Configure in Fields manager')}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default BudgetStructureCards;
